import {Injectable} from '@angular/core';
import {combineLatest, Observable} from 'rxjs';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {select, Store} from '@ngrx/store';
import {selectAttributionYear, selectLessonsGroups, StoreState} from '@redux/reducers';
import {School} from '@model/school';
import {switchMap, take} from 'rxjs/operators';
import {AttributionService} from '@services/attribution.service';
import {saveAs} from 'file-saver';
import {LessonsGroup} from '@model/lesson-group';
import {LessonService} from '@services/lesson.service';
import {
  lessonsGroupsActions
  } from '@redux/actions/attributions.action';

@Injectable()
export class AttributionFacadeService {

  school$: Observable<School>;
  attributionYear$: Observable<number>;
  lessonsGroups$: Observable<LessonsGroup[]>;

  constructor(private _store: Store<StoreState>,
              private _sharedFacade: SharedFacadeService,
              private _attributionService: AttributionService,
              private _lessonService: LessonService,
              ) {
    this.school$ = this._sharedFacade.school$;
    this.attributionYear$ = this._store.pipe(selectAttributionYear);
    this.lessonsGroups$ = this._store.pipe(select(selectLessonsGroups));
  }

  downloadAttributionsAsPDF() {
    combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, year]) => this._attributionService.downloadAttributionsPDF(school.id, year))
    ).subscribe(file => {
      saveAs(file, `attributions.pdf`);
    })
  }

  loadLessonsGroups() {
    combineLatest([this.school$, this.attributionYear$]).pipe(
      take(1),
      switchMap(([school, attributionYear]) => this._lessonService.loadLessonsGroups(school.id, attributionYear))
    ).subscribe(lessonsGroups => {
      this._store.dispatch(lessonsGroupsActions.setLessonsGroups({lessonsGroups: lessonsGroups}));
    });
  }

  saveLessonsGroup(lessonsGroup: LessonsGroup) {
    if (lessonsGroup.id) {
      this.updateLessonsGroup(lessonsGroup);
    } else {
      this.createLessonsGroup(lessonsGroup);
    }
  }

  private createLessonsGroup(lessonsGroup: LessonsGroup) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._lessonService.createLessonsGroup(school.id, lessonsGroup))
    ).subscribe(id => {
      this._store.dispatch(lessonsGroupsActions.addLessonsGroup({lessonsGroup: {...lessonsGroup, id: id, lessonsIds: []}}));
    });
  }

  private updateLessonsGroup(lessonsGroup: LessonsGroup) {
    this.school$.pipe(
      take(1),
      switchMap(school => {
        return this._lessonService.saveLessonsGroup(school.id, lessonsGroup.id, {
          subject: lessonsGroup.subject,
          type: lessonsGroup.type,
          referenceFileCode: lessonsGroup.referenceFileCode
        });
      })
    ).subscribe(_ => {
      this._store.dispatch(lessonsGroupsActions.addLessonsGroup({lessonsGroup: lessonsGroup}));
    });
  }

  deleteLessonsGroup(lessonsGroupId: number) {
    this.school$.pipe(
      take(1),
      switchMap((school) => this._lessonService.deleteLessonsGroup(school.id, lessonsGroupId))
    ).subscribe(_ => {
      this._store.dispatch(lessonsGroupsActions.deleteLessonsGroup({lessonsGroupId: lessonsGroupId}));
    });
  }

  addLessonInLessonsGroup(lessonsGroupId: number, lessonId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._lessonService.addLessonInLessonsGroup(school.id, lessonsGroupId, lessonId))
    ).subscribe(_ => {
      this._store.dispatch(lessonsGroupsActions.addLessonInLessonsGroup({lessonsGroupId: lessonsGroupId, lessonId: lessonId}));
    });
  }

  removeLessonInLessonsGroup(lessonsGroupId: number, lessonId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._lessonService.removeLessonInLessonsGroup(school.id, lessonsGroupId, lessonId))
    ).subscribe(_ => {
      this._store.dispatch(lessonsGroupsActions.deleteLessonInLessonsGroup({lessonsGroupId: lessonsGroupId, lessonId: lessonId}));
    });
  }
}
